import type { AttendanceEmployeeRef, AttendanceRecord } from '@erp/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/shadcn/index.js';
import { Badge, Button, confirmDialog, FormField, Input } from '../../../components/ui/index.js';
import {
  attendanceErrorCode,
  createAttendanceRecord,
  deleteAttendanceRecord,
  formatMinutes,
  updateAttendanceRecord,
} from '../../../lib/attendance.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: AttendanceEmployeeRef;
  date: string;
  sessions: AttendanceRecord[];
  canManage: boolean;
  onChanged: () => void | Promise<void>;
}

interface Draft { checkIn: string; checkOut: string; note: string }

/** Length of a closed session in minutes, from the shop-local strings the API sent. */
function sessionMinutes(s: AttendanceRecord): number | null {
  if (!s.checkOutAt) return null;
  return Math.round((new Date(s.checkOutAt).getTime() - new Date(s.checkInAt).getTime()) / 60_000);
}

/**
 * One person's sessions on one day — read by anyone who can see the board,
 * corrected by a manager: add the punch somebody forgot, fix a wrong time,
 * delete a double. Every change re-judges the day on the server.
 */
export function SessionsDialog({ open, onOpenChange, employee, date, sessions, canManage, onChanged }: Props): JSX.Element {
  const { t } = useTranslation('hr');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({ checkIn: '', checkOut: '', note: '' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setEditing(null); setAdding(false); }
  }, [open]);

  const fail = (err: unknown): void => {
    const code = attendanceErrorCode(err);
    toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
  };

  const startEdit = (s: AttendanceRecord): void => {
    setAdding(false);
    setEditing(s.id);
    setDraft({ checkIn: s.checkIn, checkOut: s.checkOut ?? '', note: s.note ?? '' });
  };

  const startAdd = (): void => {
    setEditing(null);
    setAdding(true);
    setDraft({ checkIn: `${date}T09:00`, checkOut: '', note: '' });
  };

  const save = async (): Promise<void> => {
    if (!draft.checkIn) return;
    setSaving(true);
    try {
      if (adding) {
        await createAttendanceRecord({
          employeeId: employee.id,
          checkIn: draft.checkIn,
          checkOut: draft.checkOut || null,
          note: draft.note.trim() || null,
        });
      } else if (editing) {
        await updateAttendanceRecord(editing, {
          checkIn: draft.checkIn,
          checkOut: draft.checkOut || null,
          note: draft.note.trim() || null,
        });
      }
      toast.success(t('attendance.sessions.saved'));
      setAdding(false);
      setEditing(null);
      await onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: AttendanceRecord): Promise<void> => {
    if (!(await confirmDialog({ message: t('attendance.sessions.confirmDelete'), tone: 'danger' }))) return;
    try {
      await deleteAttendanceRecord(s.id);
      toast.success(t('attendance.sessions.deleted'));
      await onChanged();
    } catch (err) {
      fail(err);
    }
  };

  const form = (
    <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-bg-subtle p-3 sm:grid-cols-2">
      <FormField id="att-in" label={t('attendance.sessions.checkIn')} required>
        <Input
          id="att-in"
          type="datetime-local"
          value={draft.checkIn}
          onChange={(e) => setDraft({ ...draft, checkIn: e.target.value })}
          required
        />
      </FormField>
      <FormField id="att-out" label={t('attendance.sessions.checkOut')} helperText={t('attendance.sessions.stillInHint')}>
        <Input
          id="att-out"
          type="datetime-local"
          value={draft.checkOut}
          onChange={(e) => setDraft({ ...draft, checkOut: e.target.value })}
        />
      </FormField>
      <div className="sm:col-span-2">
        <FormField id="att-note" label={t('attendance.sessions.note')}>
          <Input id="att-note" value={draft.note} maxLength={500} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
        </FormField>
      </div>
      <div className="flex gap-2 sm:col-span-2">
        <Button size="sm" loading={saving} onClick={() => void save()} disabled={!draft.checkIn}>
          {t('attendance.sessions.save')}
        </Button>
        <Button size="sm" variant="secondary" disabled={saving} onClick={() => { setAdding(false); setEditing(null); }}>
          {t('attendance.sessions.cancel')}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('attendance.sessions.title', { name: employee.displayName, date })}</DialogTitle>
          <DialogDescription>
            <span className="font-mono text-xs">{employee.code}</span>
            {employee.scheduleName ? <> · {employee.scheduleName}</> : null}
          </DialogDescription>
        </DialogHeader>

        {sessions.length === 0 && !adding && (
          <p className="text-sm text-fg-muted">{t('attendance.sessions.empty')}</p>
        )}

        <ul className="flex flex-col gap-2">
          {sessions.map((s) => {
            const mins = sessionMinutes(s);
            if (editing === s.id) return <li key={s.id}>{form}</li>;
            return (
              <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2">
                <span className="font-mono text-sm tabular-nums" dir="ltr">
                  {s.checkIn.slice(11)} – {s.checkOut ? s.checkOut.slice(11) : '…'}
                </span>
                {s.checkOut && s.checkOut.slice(0, 10) !== s.checkIn.slice(0, 10) && (
                  <span className="text-xs text-fg-muted" dir="ltr">({s.checkOut.slice(0, 10)})</span>
                )}
                {mins !== null
                  ? <span className="font-mono text-xs text-fg-muted tabular-nums">{formatMinutes(mins)}</span>
                  : <Badge tone="info">{t('attendance.sessions.stillIn')}</Badge>}
                <Badge>{t(`attendance.source.${s.checkInSource}`)}</Badge>
                {s.note && <span className="min-w-0 flex-1 truncate text-xs text-fg-muted" title={s.note}>{s.note}</span>}
                {canManage && (
                  <span className="ms-auto flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(s)} title={t('attendance.sessions.edit')} aria-label={t('attendance.sessions.edit')}>
                      <Pencil className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => void remove(s)} title={t('attendance.sessions.delete')} aria-label={t('attendance.sessions.delete')}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {adding && form}
        {canManage && !adding && !editing && (
          <div>
            <Button size="sm" variant="secondary" onClick={startAdd} className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden />
              {t('attendance.sessions.add')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
